import styled from '@emotion/styled';
import { Model } from '@gameonly/core';
import { useTranslation } from 'react-i18next';
import { GREEN, MEDIUM_BLUE, PINK, PURPLE, YELLOW } from '../colors';
import { Bottom, Title } from '../layout';
import {
  getResultFullPageTitleTranslationPrefix,
  PageLayout,
  ResultFullPageType,
} from './ResultFullPage';
import { getSubtitleTitleTranslationPrefix } from './ResultsDetailedPage';
import {
  getSubtitleResult,
  getTitleResult,
  PageType,
  PaginatedResult,
} from '../utils';
import { Footer } from './Footer';
import { getIndicatorPageTitleTranslationKey } from './IndicatorPage';

interface SummaryProps {
  paginatedResults: PaginatedResult[];
  year: number | undefined;
  model?: Model;
}

interface SummaryEntry {
  entry: string;
  pageNumber: number;
}

const sectionColors = [GREEN, MEDIUM_BLUE, PURPLE, PINK, YELLOW];

const Main = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  gap: 2.5rem;
  width: 100%;
`;

const Section = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  width: 100%;
`;

const SectionNumber = styled.p`
  font-size: 7rem;
  font-weight: bold;
  min-width: 10rem;
  text-align: right;
  border-right: 4px solid black;
  padding-right: 0px;
  line-height: 1;
  margin: 0;
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.2rem;
`;

const Entry = styled.div<{ isFirst: boolean }>`
  font-weight: ${({ isFirst }) => (isFirst ? 'bold' : 'normal')};
  font-size: ${({ isFirst }) => (isFirst ? '1.1rem' : '0.9rem')};
  color: ${({ isFirst }) => (isFirst ? 'black' : '#999')};
  display: flex;
`;

const PageNumber = styled.span`
  color: #black;
  min-width: 2.5rem;
`;

const Summary = ({ paginatedResults, year, model }: SummaryProps) => {
  const { t } = useTranslation(['project']);
  if (!model) return null;
  const summary = generateSummary(paginatedResults, model);
  return (
    <PageLayout backgroundColor="white" backgroundVariant='bottomLeft' withBackgroundImage>
      <Title>{t('project.pdf.summary')}</Title>
      <Main>
        {summary.map((section, sectionIdx) => {
          const color = sectionColors[sectionIdx % sectionColors.length];
          const formattedNumber = String(sectionIdx + 1).padStart(2, '0');

          return (
            <Section key={`section-${sectionIdx}`}>
              <SectionNumber style={{ color }}>{formattedNumber}</SectionNumber>
              <SectionContent>
                {section.map((entry, entryIdx) => (
                  <Entry key={`entry-${entryIdx}`} isFirst={entryIdx === 0}>
                    <PageNumber>
                      {`p.${entry.pageNumber}`.toLocaleUpperCase()}
                    </PageNumber>
                    {entryIdx === 0
                      ? t(entry.entry).toLocaleUpperCase()
                      : t(entry.entry)}
                  </Entry>
                ))}
              </SectionContent>
            </Section>
          );
        })}
      </Main>
      <Bottom>
        <Footer year={year} backgroundType="light" />
      </Bottom>
    </PageLayout>
  );
};

const createEntry = (
  paginatedResult: PaginatedResult,
  model: Model,
): string | null => {
  // Placeholder logic – replace with actual entry generation logic
  switch (paginatedResult.pageType) {
    case PageType.COVER_COMPLETE:
    case PageType.COVER_SIMPLE:
    case PageType.SUMMARY:
    case PageType.BACK_COVER:
      return null;
    case PageType.MAIN_INDICATOR:
      const titleResult = getTitleResult(paginatedResult.results);
      if (!titleResult) return null;
      else return getIndicatorPageTitleTranslationKey(titleResult, model.name);
    case PageType.RESULT_FULL_PAGE:
      const result = paginatedResult.results
        ? (paginatedResult.results[0] as ResultFullPageType)
        : null;
      if (!result) return null;
      else return getResultFullPageTitleTranslationPrefix(result, model.name);
    case PageType.RESULTS_DETAILED:
      const subtitleResult = getSubtitleResult(paginatedResult.results);
      if (!subtitleResult) return null;
      else return getSubtitleTitleTranslationPrefix(subtitleResult, model.name);
    default:
      return null;
  }
};

function generateSummary(
  paginatedResults: PaginatedResult[],
  model: Model,
): SummaryEntry[][] {
  const summary: SummaryEntry[][] = [];
  const tableGroup: SummaryEntry[] = [];

  let currentGroup: SummaryEntry[] | null = null;
  let lastEntry: string | null = null;

  for (const paginatedResult of paginatedResults) {
    const { pageType, results, pageNumber = 0 } = paginatedResult;

    // Skip specified page types
    if (
      pageType === PageType.COVER_COMPLETE ||
      pageType === PageType.COVER_SIMPLE ||
      pageType === PageType.SUMMARY ||
      pageType === PageType.BACK_COVER
    ) {
      continue;
    }

    const isTableResult =
      pageType === PageType.RESULT_FULL_PAGE && results?.[0]?.type === 'table';

    if (isTableResult) {
      const entry = createEntry(paginatedResult, model);
      if (entry && !tableGroup.some((e) => e.entry === entry)) {
        tableGroup.push({ entry, pageNumber });
      }
      continue; // Skip further processing for table entries
    }

    const isStartOfNewGroup = pageType === PageType.MAIN_INDICATOR;

    if (isStartOfNewGroup) {
      if (currentGroup && currentGroup.length > 0) {
        summary.push(currentGroup);
      }
      currentGroup = [];
      lastEntry = null;
    }

    const entry = createEntry(paginatedResult, model);
    if (!entry || currentGroup === null) continue;

    if (entry !== lastEntry) {
      currentGroup.push({ entry, pageNumber });
      lastEntry = entry;
    }
  }

  // Push the last regular group
  if (currentGroup && currentGroup.length > 0) {
    summary.push(currentGroup);
  }

  // Push table group as final group if not empty
  if (tableGroup.length > 0) {
    summary.push(tableGroup);
  }

  return summary;
}

export default Summary;
